import React, { useState } from 'react';
import { Box, Typography, Pressable } from '@/shared/design-system/primitives';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { formatMacro } from '@shared/utils/formatting';
import { useHubUIStore } from '../../../stores/v4/useHubUIStore';
import { PillarMetric } from '../hooks/useMetabolicMatrix';
import { SourceAuditList } from './SourceAuditList';
import { NUTRIENT_REGISTRY, normalizeNutrientKey } from '@/shared/constants/metabolic-pillars';

interface OtherCompoundsCardProps {
    metrics: PillarMetric[];
}

/**
 * OtherCompoundsCard: A specialized Hub card for non-pillar nutrients.
 * V8.4: Uses NUTRIENT_REGISTRY for pretty labels.
 */
export const OtherCompoundsCard: React.FC<OtherCompoundsCardProps> = ({ metrics }) => {
    const isCollapsed = useHubUIStore(state => state.collapsedSections['OTHER_COMPOUNDS'] || false);
    const toggleSection = useHubUIStore(state => state.toggleSection);

    const [expandedMetricId, setExpandedMetricId] = useState<string | null>(null);
    const numericStyle = { fontVariantNumeric: 'lining-nums', fontFamily: 'ui-sans-serif, system-ui, sans-serif' };

    const handleToggleAudit = (id: string) => {
        setExpandedMetricId(expandedMetricId === id ? null : id);
    };

    return (
        <Box style={{ 
            backgroundColor: 'rgba(255,255,255,0.02)', 
            borderBottomWidth: 1, 
            borderBottomColor: isCollapsed ? 'rgba(255,255,255,0.05)' : '#06b6d422',
            marginBottom: 12,
            overflow: 'hidden'
        }}>
            <Pressable 
                onPress={() => toggleSection('OTHER_COMPOUNDS')}
                style={{ padding: 20, display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: isCollapsed ? 'transparent' : 'rgba(255,255,255,0.01)' }}
            >
                <Typography style={{ fontSize: 11, fontWeight: '950', color: '#06b6d4', letterSpacing: 1.5 }}>OTHER ACTIVE COMPOUNDS</Typography>
                
                <Box style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                    {isCollapsed && (
                        <Typography style={{ ...numericStyle, fontSize: 16, fontWeight: '950', color: '#06b6d4', marginTop: 1 }}>{metrics.length} ITEMS</Typography>
                    )}
                    {isCollapsed ? <ChevronRight size={16} color="#444" /> : <ChevronDown size={16} color="#06b6d4" />}
                </Box>
            </Pressable>

            {!isCollapsed && (
                <Box style={{ paddingHorizontal: 20, paddingBottom: 20, paddingTop: 5, display: 'flex', flexDirection: 'column', gap: 15 }}>
                    {metrics.map(m => {
                        const isExpanded = expandedMetricId === m.id;
                        const profile = NUTRIENT_REGISTRY[normalizeNutrientKey(m.id)];
                        const prettyLabel = profile?.label || m.label;
                        const subLabel = profile?.subLabel;
                        
                        return (
                            <Box key={m.id}>
                                <Pressable onPress={() => handleToggleAudit(m.id)} style={{ width: '100%' }}>
                                    <Box style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: 6, alignItems: 'center' }}>
                                        <Box>
                                            <Typography style={{ fontSize: 12, fontWeight: '900', color: isExpanded ? '#fff' : '#888', textTransform: 'uppercase' }}>{prettyLabel}</Typography>
                                            {subLabel && (
                                                <Typography style={{ fontSize: 8, fontWeight: '800', color: '#444', textTransform: 'uppercase', marginTop: 1 }}>{subLabel}</Typography>
                                            )}
                                        </Box>
                                        <Typography style={{ ...numericStyle, fontSize: 16, fontWeight: '950', color: '#fff', marginTop: 1 }}>
                                            {formatMacro(m.value)}<Typography style={{ ...numericStyle, fontSize: 11, color: '#444', fontWeight: '800' }}> {m.unit}</Typography>
                                        </Typography>
                                    </Box>
                                </Pressable>

                                {/* INLINE SOURCE AUDIT */}
                                <SourceAuditList 
                                    sources={m.sources || []} 
                                    unit={m.unit} 
                                    color="#06b6d4" 
                                    isVisible={isExpanded} 
                                />
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
};
